import React from 'react';
import { Grid, HStack, Text, type TextProps, VStack } from '@chakra-ui/react';
import { type IconButtonProps } from '@/components/ui/icon-button';
import { CloseButton } from '@/components/ui/close-button.tsx';
import { Tooltip } from '@/components/ui/tooltip';
import { Title } from '@/components/ui/title';

interface DialogHeaderProps {
	title: React.ReactNode;
	onClose?: () => void;
	actions?: React.ReactNode;
	closeAction?: React.ReactNode;
	titleProps?: TextProps;
	subtitle?: React.ReactNode;
	subtitleProps?: TextProps;
	subtitleTooltip?: React.ReactNode;
}

export const dialogHeaderActionButtonProps: IconButtonProps = {
	variant: 'ghost',
	borderRadius: 'full',
	boxSize: { base: '40px', md: '32px' },
	minW: { base: '40px', md: '32px' },
	p: 0,
	color: 'fg.muted',
	_hover: { bg: 'surface.subtle' },
};

const DialogHeader: React.FC<DialogHeaderProps> = ({
	title,
	onClose,
	actions,
	closeAction,
	titleProps,
	subtitle,
	subtitleProps,
	subtitleTooltip,
}) => (
	// With a subtitle the header is two lines, so align the action icons to the title row (start)
	// rather than the vertical middle of the whole block; single-line headers stay centered.
	<Grid templateColumns="minmax(0, 1fr) auto" alignItems={subtitle ? 'start' : 'center'} px={5} pt={5} pb={4} gap={2}>
		<VStack align="start" gap={1} minW={0} pr={1}>
			<Title minW={0} level="dialog" lineHeight="1.15" {...titleProps}>
				{title}
			</Title>
			{subtitle && (
				<Tooltip content={subtitleTooltip} disabled={!subtitleTooltip} openDelay={300}>
					<Text
						minW={0}
						maxW="full"
						textStyle="body.sm"
						color="fg.muted"
						lineHeight="1.35"
						truncate
						{...subtitleProps}
					>
						{subtitle}
					</Text>
				</Tooltip>
			)}
		</VStack>
		<HStack gap={1} justify="flex-end" flexShrink={0} align="center">
			{actions}
			{closeAction ?? (onClose && <CloseButton {...dialogHeaderActionButtonProps} onClick={onClose} />)}
		</HStack>
	</Grid>
);

export default DialogHeader;
